import { WebglHelper } from './webglHelper';
import { Webgl2Pipeline } from './webgl2Pipeline';
export class ResizingStage {
    private vbWebgl2: Webgl2Pipeline;
    private vbHelper: WebglHelper;
    private videoOutputPixels: Uint8Array;
    private videoFrameBuffer: WebGLFramebuffer;
    private program: WebGLProgram;
    private fragmentShader: WebGLShader;
    private videoTexture: WebGLTexture;
    private gl: WebGL2RenderingContext;
    private segmentationMaskWidth: number;
    private segmentationMaskHeight: number;

    /**
     * @function create
     * @static
     * @public
     * @param {Webgl2Pipeline} vbWebgl2 The instance of the webgl2 class
     * @returns {ResizingStage} Initialization of the class
     */
    public static create(vbWebgl2: Webgl2Pipeline): ResizingStage { return new ResizingStage(vbWebgl2); }

    private constructor(vbWebgl2: Webgl2Pipeline) {
        this.vbWebgl2 = vbWebgl2;
        this.vbHelper = WebglHelper.create();
        this.gl = this.vbWebgl2.gl;
        this.segmentationMaskWidth = this.vbWebgl2.segmentationMaskCanvas.width;
        this.segmentationMaskHeight = this.vbWebgl2.segmentationMaskCanvas.height;

        const fragmentShaderSource = String.raw`#version 300 es
        precision highp float;
        
        uniform sampler2D u_inputFrame;
        
        in vec2 v_texCoord;
        
        out vec4 outColor;
        
        void main(){
            outColor = texture(u_inputFrame, v_texCoord);
        }`;

        this.fragmentShader = this.vbHelper.initShader(this.gl, this.gl.FRAGMENT_SHADER, fragmentShaderSource);

        // Use gl program to fill videoTexture (segmentation sized)
        this.program = this.vbHelper.initProgram(this.gl, this.vbWebgl2.vertexShader, this.fragmentShader, this.vbWebgl2.positionBuffer, this.vbWebgl2.texCoordBuffer);


        // Create the videoTexture
        this.videoTexture = this.vbHelper.createTexture(this.gl, this.gl.RGBA8, this.segmentationMaskWidth, this.segmentationMaskHeight);
        this.videoFrameBuffer = this.gl.createFramebuffer();
        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.videoFrameBuffer);
        this.gl.framebufferTexture2D(this.gl.FRAMEBUFFER, this.gl.COLOR_ATTACHMENT0, this.gl.TEXTURE_2D, this.videoTexture, 0);

        // Create video resized output pixel array
        this.videoOutputPixels = new Uint8Array(this.vbWebgl2.segmentationPixelCount * 4);

        // Initialize video sample
        const inputFrameLocation = this.gl.getUniformLocation(this.program, 'u_inputFrame');
        this.gl.useProgram(this.program);
        this.gl.uniform1i(inputFrameLocation, 0);
    }

    /****************************************************************************/
    /** PUBLIC STUFF                                                           **/
    /****************************************************************************/

    /**
     * @function render
     * @async
     * @public
     * @description resizes each received image to a smaller size and writes the data to a buffer in tflite
     */
    public async render(): Promise<void> {
        if (this.vbWebgl2.statsEnabled) {
            const t0 = performance.now();
            this.gl.viewport(0, 0, this.segmentationMaskWidth, this.segmentationMaskHeight);
            this.gl.useProgram(this.program);

            // Binding videoFrameBuffer as source for readPixels method
            this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.videoFrameBuffer);
            this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

            // Copy into buf the pixels of frameBuffer
            const buf = this.gl.createBuffer();
            this.gl.bindBuffer(this.gl.PIXEL_PACK_BUFFER, buf);
            this.gl.bufferData(this.gl.PIXEL_PACK_BUFFER, this.videoOutputPixels.byteLength, this.gl.STREAM_READ);
            this.gl.readPixels(0, 0, this.segmentationMaskWidth, this.segmentationMaskHeight, this.gl.RGBA, this.gl.UNSIGNED_BYTE, 0);
            this.gl.bindBuffer(this.gl.PIXEL_PACK_BUFFER, null);

            const t1 = performance.now();

            // Wait for GPU and Browser repaint loop are synchronized and copy current video frame in outPutPixels
            await this.getBufferSubDataAsync(buf, this.videoOutputPixels);
            this.gl.deleteBuffer(buf);

            const t2 = performance.now();

            // Copy resized input video pixels in tflite input memory
            const tflite = this.vbWebgl2.tflite;
            const inputMemoryOffset = tflite._getInputMemoryOffset() / 4;

            const t3 = performance.now();

            for (let i = 0; i < this.vbWebgl2.segmentationPixelCount; i++) {
                const tfliteIndex = inputMemoryOffset + i * 3;
                const outputIndex = i * 4;
                tflite.HEAPF32[tfliteIndex] = this.videoOutputPixels[outputIndex] / 255;
                tflite.HEAPF32[tfliteIndex + 1] = this.videoOutputPixels[outputIndex + 1] / 255;
                tflite.HEAPF32[tfliteIndex + 2] = this.videoOutputPixels[outputIndex + 2] / 255;
            }
            const t4 = performance.now();

            this.vbWebgl2.stats.value.initialisation += t1 - t0;
            this.vbWebgl2.stats.value.synchro += t2 - t1;
            this.vbWebgl2.stats.value.copy += t3 - t2;
            this.vbWebgl2.stats.value.ecriture += t4 - t3;
        }
        else {
            this.gl.viewport(0, 0, this.segmentationMaskWidth, this.segmentationMaskHeight);
            this.gl.useProgram(this.program);

            // Binding videoFrameBuffer as source for readPixels method
            this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, this.videoFrameBuffer);
            this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);

            // Copy into buf the pixels of frameBuffer
            const buf = this.gl.createBuffer();
            this.gl.bindBuffer(this.gl.PIXEL_PACK_BUFFER, buf);
            this.gl.bufferData(this.gl.PIXEL_PACK_BUFFER, this.videoOutputPixels.byteLength, this.gl.STREAM_READ);
            this.gl.readPixels(0, 0, this.segmentationMaskWidth, this.segmentationMaskHeight, this.gl.RGBA, this.gl.UNSIGNED_BYTE, 0);
            this.gl.bindBuffer(this.gl.PIXEL_PACK_BUFFER, null);

            // Wait for GPU and Browser repaint loop are synchronized and copy current video frame in outPutPixels
            await this.getBufferSubDataAsync(buf, this.videoOutputPixels);
            this.gl.deleteBuffer(buf);

            // Copy resized input video pixels in tflite input memory
            const tflite = this.vbWebgl2.tflite;
            const inputMemoryOffset = tflite._getInputMemoryOffset() / 4;
            for (let i = 0; i < this.vbWebgl2.segmentationPixelCount; i++) {
                const tfliteIndex = inputMemoryOffset + i * 3;
                const outputIndex = i * 4;
                tflite.HEAPF32[tfliteIndex] = this.videoOutputPixels[outputIndex] / 255;
                tflite.HEAPF32[tfliteIndex + 1] = this.videoOutputPixels[outputIndex + 1] / 255;
                tflite.HEAPF32[tfliteIndex + 2] = this.videoOutputPixels[outputIndex + 2] / 255;
            }
        }
    }

    /**
     * @function cleanUp
     * @public
     * @description removes the different components used to resize the mask
     */
    public cleanUp(): void {
        if (this.videoFrameBuffer) this.gl.deleteFramebuffer(this.videoFrameBuffer);
        if (this.videoTexture) this.gl.deleteTexture(this.videoTexture);
        if (this.program) this.gl.deleteProgram(this.program);
        if (this.fragmentShader) this.gl.deleteShader(this.fragmentShader);
    }

    /****************************************************************************/
    /** PRIVATE STUFF                                                          **/
    /****************************************************************************/

    /**
     * @function getBufferSubDataAsync
     * @async
     * @private
     * @param {WebGLBuffer} buffer The buffer containing the video data
     * @param {ArrayBufferView} dstBuffer The destination buffer, which will receive the video data
     * @description Requires the execution of all pending tasks before synchronizing the two processors and sending the data to a buffer
     */
    private async getBufferSubDataAsync(buffer: WebGLBuffer, dstBuffer: ArrayBufferView): Promise<void> {
        const sync = this.gl.fenceSync(this.gl.SYNC_GPU_COMMANDS_COMPLETE, 0);

        //Causes the remaining commands to be executed as quickly as possible to free up the GPU
        this.gl.flush();

        //Synchronizes the 2 processors
        const res = await this.clientWaitAsync(sync);
        this.gl.deleteSync(sync);

        //Transmits data to the CPU
        if (res !== this.gl.WAIT_FAILED) {
            this.gl.bindBuffer(this.gl.PIXEL_PACK_BUFFER, buffer);
            this.gl.getBufferSubData(this.gl.PIXEL_PACK_BUFFER, 0, dstBuffer);
            this.gl.bindBuffer(this.gl.PIXEL_PACK_BUFFER, null);
        }
    }

    /**
     * @function clientWaitAsync
     * @private
     * @param {WebGLSync} sync the WebGLSync object
     * @description synchronizes both processors (CPU and GPU)
     * @returns {number} Returns gl.CONDITION_SATISFIED if the synchronization was successful, and gl.WAIT_FAILED if there was an error
     */
    private clientWaitAsync(sync: WebGLSync): Promise<number> {
        return new Promise<number>((resolve) => {
            const test = () => {
                //Waits for the synchronization of the two processors
                const res = this.gl.clientWaitSync(sync, 0, 0);
                if (res === this.gl.WAIT_FAILED) { resolve(res); return; }
                if (res === this.gl.TIMEOUT_EXPIRED) { requestAnimationFrame(test); return; }
                resolve(res);
            };
            requestAnimationFrame(test);
        });
    }

}